/* ───────────────────────────────────────────────────────────────────────────
   Essays — the native NM Squad essay tab inside StudentPortal.
   Students submit college essays; Neeraj (team) records voice + text feedback.
   Also shows the student's older essays from the OTG essay tool, read-only.
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect, useRef } from "react";
import {
  ESSAY_TYPES, MAX_AUDIO_SEC, addEssay, subscribeStudentEssays,
  subscribeAllFeedback, addEssayFeedback,
} from "./essays";
import { fetchOtgEssays, fetchOtgFeedback, essayMatchesStudent } from "./otgEssays";

const GOLD_LO = "#8A6400";
const INK = "#1A1A1E", TXT = "#5C5C66", MUTE = "#9A9AA2";
const LINE = "#E6E6E9", BG = "#F6F6F7";

const wc = s => (s && s.trim() ? s.trim().split(/\s+/).length : 0);
function fmtDate(t) {
  if (!t) return "";
  const d = t.toDate ? t.toDate() : new Date(t);
  return isNaN(d) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function mmss(s) {
  const m = Math.floor(s / 60), ss = s % 60;
  return m + ":" + String(ss).padStart(2, "0");
}
const lab = { fontSize: 10, fontWeight: 700, fontFamily: "'Space Mono',monospace",
  textTransform: "uppercase", letterSpacing: ".06em", color: MUTE, display: "block",
  marginBottom: 4 };

/* ── Submit form ── */
function EssayForm({ student, onDone }) {
  const [d, setD] = useState({ college: "", question: "", wordLimit: "", type: "",
    essay: "", commentsForNM: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const words = wc(d.essay);
  const limit = parseInt(d.wordLimit, 10);
  const over = limit && words > limit;
  const ready = d.essay.trim() && d.type;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    try {
      await addEssay(student.id, student.studentName, { ...d, wordCount: words });
      onDone();
    } catch (e) { alert("Could not submit. Please try again."); }
    setBusy(false);
  }
  return (
    <div className="sp-card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 12 }}>
        Submit an essay</div>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))" }}>
        <div><span style={lab}>College</span>
          <input className="sp-in" value={d.college} placeholder="College, or NA"
            onChange={e => set("college", e.target.value)} /></div>
        <div><span style={lab}>Essay type</span>
          <select className="sp-in" value={d.type} onChange={e => set("type", e.target.value)}>
            <option value="">Select…</option>
            {ESSAY_TYPES.map(o => <option key={o}>{o}</option>)}
          </select></div>
        <div><span style={lab}>Word limit</span>
          <input className="sp-in" value={d.wordLimit} placeholder="e.g. 650" inputMode="numeric"
            onChange={e => set("wordLimit", e.target.value.replace(/\D/g, ""))} /></div>
      </div>
      <div style={{ marginTop: 12 }}>
        <span style={lab}>The prompt / question</span>
        <textarea className="sp-in" rows={2} value={d.question}
          onChange={e => set("question", e.target.value)} style={{ resize: "vertical" }} /></div>
      <div style={{ marginTop: 12 }}>
        <span style={lab}>Your essay</span>
        <textarea className="sp-in" rows={10} value={d.essay}
          onChange={e => set("essay", e.target.value)}
          style={{ resize: "vertical", lineHeight: 1.6 }} />
        <div style={{ fontSize: 11.5, fontWeight: 800, marginTop: 4,
          color: over ? "#dc2626" : MUTE }}>
          {words}{limit ? ` / ${limit}` : ""} words · {d.essay.length} characters
          {over ? " — over the limit" : ""}</div>
      </div>
      <div style={{ marginTop: 12 }}>
        <span style={lab}>Comments for Neeraj sir (optional)</span>
        <textarea className="sp-in" rows={2} value={d.commentsForNM}
          onChange={e => set("commentsForNM", e.target.value)} style={{ resize: "vertical" }} /></div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button className="sp-btn gold" disabled={!ready || busy} onClick={submit}
          style={{ opacity: !ready || busy ? 0.55 : 1 }}>
          {busy ? "Submitting…" : "Submit essay"}</button>
        <button className="sp-btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* ── Voice + text feedback recorder (team) ── */
function FeedbackRecorder({ essay, onClose }) {
  const [notes, setNotes] = useState("");
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioData, setAudioData] = useState("");
  const [saving, setSaving] = useState(false);
  const mr = useRef(null), rec = useRef(null), stream = useRef(null);
  const chunks = useRef([]), timer = useRef(null);

  function stop() {
    clearInterval(timer.current);
    try { if (mr.current && mr.current.state !== "inactive") mr.current.stop(); } catch (e) {}
    try { if (rec.current) rec.current.stop(); } catch (e) {}
    setRecording(false);
  }
  async function start() {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      let mime = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
      const m = new MediaRecorder(s, { mimeType: mime, audioBitsPerSecond: 24000 });
      chunks.current = [];
      m.ondataavailable = e => { if (e.data && e.data.size) chunks.current.push(e.data); };
      m.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const fr = new FileReader();
        fr.onload = () => setAudioData(String(fr.result));
        fr.readAsDataURL(blob);
        if (stream.current) stream.current.getTracks().forEach(t => t.stop());
      };
      mr.current = m;
      m.start();
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        const r = new SR();
        r.continuous = true; r.interimResults = true; r.lang = "en-IN";
        r.onresult = e => {
          let fin = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            if (e.results[i].isFinal) fin += e.results[i][0].transcript;
          }
          if (fin) setTranscript(t => (t + " " + fin).trim());
        };
        r.onerror = () => {};
        rec.current = r;
        try { r.start(); } catch (e) {}
      }
      setRecording(true);
      setSeconds(0);
      timer.current = setInterval(() => {
        setSeconds(x => {
          if (x + 1 >= MAX_AUDIO_SEC) { stop(); return MAX_AUDIO_SEC; }
          return x + 1;
        });
      }, 1000);
    } catch (e) {
      alert("Could not access the microphone. Please allow microphone access and try again.");
    }
  }
  useEffect(() => () => { clearInterval(timer.current);
    if (stream.current) stream.current.getTracks().forEach(t => t.stop()); }, []);

  async function save() {
    if (!audioData && !notes.trim()) return;
    setSaving(true);
    try {
      await addEssayFeedback(essay, { audioData, transcript: transcript.trim(), notes: notes.trim() });
      onClose();
    } catch (e) {
      alert("Could not save — the voice note may be too long. Record a shorter note.");
    }
    setSaving(false);
  }

  return (
    <div style={{ marginTop: 12, borderTop: `1.5px solid ${LINE}`, paddingTop: 13 }}>
      <span style={lab}>Add feedback</span>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        marginBottom: 10 }}>
        {recording ? (
          <button className="sp-btn" style={{ background: "#dc2626", borderColor: "#dc2626",
            color: "#fff" }} onClick={stop}>Stop recording</button>
        ) : (
          <button className="sp-btn gold" onClick={start}>
            {audioData ? "Re-record voice note" : "Record voice note"}</button>
        )}
        {recording && (
          <span style={{ fontSize: 13, fontWeight: 800, color: "#dc2626" }}>
            ● {mmss(seconds)} / {mmss(MAX_AUDIO_SEC)}</span>
        )}
        {!recording && audioData && (
          <audio controls src={audioData} style={{ height: 34, flex: "1 1 220px" }} />
        )}
      </div>
      {(recording || transcript) && (
        <div>
          <span style={lab}>Live transcript</span>
          <div style={{ fontSize: 13, color: TXT, lineHeight: 1.55, background: BG,
            border: `1px solid ${LINE}`, borderRadius: 8, padding: "10px 12px", minHeight: 44,
            whiteSpace: "pre-wrap" }}>{transcript || "Listening…"}</div>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <span style={lab}>Text notes</span>
        <textarea className="sp-in" rows={3} value={notes}
          onChange={e => setNotes(e.target.value)} style={{ resize: "vertical" }}
          placeholder="Written feedback (optional)" />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="sp-btn gold" disabled={saving || (!audioData && !notes.trim())}
          onClick={save} style={{ opacity: saving || (!audioData && !notes.trim()) ? 0.55 : 1 }}>
          {saving ? "Saving…" : "Save feedback"}</button>
        <button className="sp-btn ghost" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/* ── Feedback block (read) ── */
function FeedbackBlock({ f }) {
  return (
    <div style={{ marginTop: 13, borderTop: `1.5px solid ${LINE}`, paddingTop: 12 }}>
      <div style={{ ...lab, color: GOLD_LO, marginBottom: 7 }}>
        Feedback{f.fromName ? ` — ${f.fromName}` : ""}</div>
      {f.audioData && (
        <audio controls src={f.audioData} style={{ width: "100%", marginBottom: 9 }} />
      )}
      {f.notes && (
        <div style={{ fontSize: 13.5, color: INK, lineHeight: 1.6,
          marginBottom: f.transcript ? 7 : 0 }}>{f.notes}</div>
      )}
      {f.transcript && (
        <div style={{ fontSize: 12.5, color: TXT, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
          {f.transcript}</div>
      )}
    </div>
  );
}

/* ── One essay card ── */
function EssayCard({ essay, feedback, team, source }) {
  const [recording, setRecording] = useState(false);
  const [open, setOpen] = useState(false);
  return (
    <div className="sp-card" style={{ padding: 18 }}>
      <div onClick={() => setOpen(o => !o)} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {essay.type && (
            <span style={{ fontSize: 10, fontFamily: "'Space Mono',monospace", fontWeight: 700,
              textTransform: "uppercase", letterSpacing: ".05em", color: GOLD_LO,
              border: `1px solid ${LINE}`, borderRadius: 5, padding: "3px 8px" }}>{essay.type}</span>
          )}
          {essay.college && (
            <span style={{ fontSize: 14, fontWeight: 800, color: INK }}>{essay.college}</span>
          )}
          {source && (
            <span style={{ fontSize: 10.5, fontWeight: 700, color: MUTE }}>{source}</span>
          )}
          {feedback.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 800, color: "#15803d" }}>● FEEDBACK</span>
          )}
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: MUTE, fontWeight: 600 }}>
            {fmtDate(essay.createdAt)}</span>
          <span style={{ fontSize: 13, color: MUTE, fontWeight: 800,
            transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
        </div>
        {essay.question && (
          <div style={{ fontFamily: "'Newsreader',serif", fontStyle: "italic", fontSize: 14,
            color: TXT, marginTop: 8,
            ...(open ? {} : { whiteSpace: "nowrap", overflow: "hidden",
              textOverflow: "ellipsis" }) }}>{essay.question}</div>
        )}
      </div>
      {open && (
        <>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.65, color: INK,
            background: BG, border: `1px solid ${LINE}`, borderRadius: 8, padding: "13px 15px",
            marginTop: 10 }}>
            {essay.essay}</div>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: MUTE, marginTop: 6 }}>
            {essay.wordCount || wc(essay.essay)}{essay.wordLimit ? ` / ${essay.wordLimit}` : ""} words</div>
          {essay.commentsForNM && (
            <div style={{ fontSize: 12.5, color: TXT, marginTop: 8 }}>
              <b style={{ color: INK }}>Student's note:</b> {essay.commentsForNM}</div>
          )}
          {feedback.map(f => <FeedbackBlock key={f.id} f={f} />)}
          {team && !source && (
            recording
              ? <FeedbackRecorder essay={essay} onClose={() => setRecording(false)} />
              : <button className="sp-btn ghost" style={{ marginTop: 12 }}
                  onClick={() => setRecording(true)}>+ Add feedback</button>
          )}
        </>
      )}
    </div>
  );
}

/* ── Main tab ── */
export function EssaysTab({ student, team }) {
  const [essays, setEssays] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [otg, setOtg] = useState([]);
  const [otgFb, setOtgFb] = useState([]);
  const [composing, setComposing] = useState(false);

  useEffect(() => subscribeStudentEssays(student.id, setEssays), [student.id]);
  useEffect(() => subscribeAllFeedback(setFeedback), []);
  useEffect(() => {
    let alive = true;
    Promise.all([fetchOtgEssays(), fetchOtgFeedback()])
      .then(([es, fb]) => {
        if (!alive) return;
        setOtg(es.filter(e => essayMatchesStudent(e, student)));
        setOtgFb(fb);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [student]);

  return (
    <div className="sp-rise">
      {!team && (
        composing
          ? <EssayForm student={student} onDone={() => setComposing(false)} />
          : <button className="sp-btn gold" style={{ marginBottom: 16 }}
              onClick={() => setComposing(true)}>+ Submit an essay</button>
      )}

      {essays.length === 0 && otg.length === 0 && (
        <div className="sp-card" style={{ padding: "32px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>No essays yet</div>
          <div style={{ fontSize: 13, color: MUTE, marginTop: 5 }}>
            {team ? "Essays the student submits appear here for your feedback."
              : "Submit your first college essay above to get feedback from Neeraj sir."}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {essays.map(e => (
          <EssayCard key={e.id} essay={e} team={team}
            feedback={feedback.filter(f => f.essayId === e.id)} />
        ))}
      </div>

      {otg.length > 0 && (
        <>
          <div style={{ ...lab, marginTop: 22, marginBottom: 10 }}>From the OTG essay tool</div>
          <div style={{ display: "grid", gap: 14 }}>
            {otg.map(e => (
              <EssayCard key={e.id} essay={e} team={team} source="OTG essay tool"
                feedback={otgFb.filter(f => f.essayId === e.id)
                  .map(f => ({ ...f, audioData: f.audioUrl || f.audioData }))} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
